import Layout from 'components/Layout/Layout';
import PageMeta from 'components/PageMeta';
import { Breadcrumb } from 'generated/graphql';
import { getPage } from 'graphql/cmsQueries';
import { GetServerSidePropsContext } from 'next';
import { getCommonServerSideProps } from 'server/getCommonServerSideProps';
import { CommonPageProps } from 'types/CommonPageProps';
import { parseContextLocale } from 'utils/parseContextLocale';
import { parseRouteParams } from 'utils/parseRouteParams';
import { getConsultantPublicContentBySlug } from 'graphql/consultantQueries';
import { extractConsultantSlug } from 'utils/extractConsultantSlug';
import { useServerTranslation } from 'hooks/useServerTranslation';
import WordpressContent from '../components/Content/WordpressContent';

type ContentProps = CommonPageProps<{
    content: string;
    slug: string;
    breadcrumbs: Breadcrumb[];
    title: string;
    elementorCss: string | null;
    elementorScripts: string[];
    elementorConfig: string | null;
}>;

export default function Page(props: ContentProps) {
    const {
        title,
        content,
        breadcrumbs,
        elementorCss,
        elementorScripts,
        elementorConfig,
    } = props;
    const { t } = useServerTranslation();

    const displayTitle = breadcrumbs.length > 0
        ? breadcrumbs[breadcrumbs.length - 1].title
        : title;

    return (
        <Layout breadcrumbs={breadcrumbs} showBreadcrumbs>
            <PageMeta
                title={t('seo.title.wp-slug', { title: displayTitle })}
                description={t('seo.description.home', { description: '' })}
            />
            <WordpressContent content={content} elementorCss={elementorCss} elementorScripts={elementorScripts} elementorConfig={elementorConfig} />
        </Layout>
    );
}

type RouteParams = {
    slug: string[];
};

export async function getServerSideProps(context: GetServerSidePropsContext<RouteParams>) {
    const { market } = await parseContextLocale(context);
    const [slug] = parseRouteParams(context, ['slug'], (slugs) => slugs.join('/'));

    const { isConsultantRoute, consultantSlug, slug: pageSlugs } = extractConsultantSlug(slug);

    if (isConsultantRoute) {
        const result = await getConsultantPublicContentBySlug(market, consultantSlug);

        if (!result?.data?.organisationPublicContent) {
            return { notFound: true };
        }
    }

    const page = await getPage(market, pageSlugs);

    if (!page) {
        return { notFound: true };
    }

    return getCommonServerSideProps({
        context,
        additionalProps: { ...page },
    });
}
